import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import moment from 'moment'
import { TransLabel } from 'components/Translate'
import { animateScroll } from 'react-scroll'
import { getProfile, getUserChatsStatus, getUserChatroom } from 'selectors'
import { getTradeIsInDispute } from "selectors/trades.selectors"
import {
    getChat,
    addFile as addFileAction,
    joinRoom,
    leaveRoom,
    markAsRead,
    markMessageAsRead,
    removeErrors,
    addChatError
} from 'actions'
import { getFileURL } from 'actions/api'
import { uploadFile as uploadFileModal } from 'actions/modals'
import { LoadingBox } from 'components'
import { renderMessage, renderFile, errorMessage } from './renders'
import { uniqBy, groupBy, isEmpty } from 'lodash'
import InputBox from './InputBox'
import { TOGGLE_MOBILE_CHAT } from 'constants/flags'
import { withGetScreen } from 'react-getscreen'

class ChatWindow extends PureComponent {
    state = { uploading: false }

    componentDidMount() {
        const { joinRoom, roomId, username, userId, role } = this.props
        joinRoom({ roomId, username, userId, role })
    }
    componentWillUnmount() {
        this.props.leaveRoom()
    }

    componentDidUpdate(prevProps) {
        const {
            chat,
            roomId,
            username,
            userId,
            role,
            joinRoom,
            isDesktop,
            showMobileChat,
            unreadNotifications,
            markAsRead,
            markMessageAsRead,
            unreadMessages,
        } = this.props
        const newMessage = prevProps.chat.messages.length !== chat.messages.length
        const newFiles = prevProps.chat.files.length !== chat.files.length
        const newContent = newMessage || newFiles
        if (prevProps.roomId !== roomId) {
            joinRoom({
                roomId: roomId,
                username: username,
                userId: userId,
                role: role
            })
        }

        if (!prevProps.showMobileChat && showMobileChat)
            animateScroll.scrollToBottom({ containerId: 'chat' })

        if (chat) {
            if (newContent) {
                const doScrollInMobile = showMobileChat && !isDesktop()
                if (doScrollInMobile || isDesktop())
                    animateScroll.scrollToBottom({ containerId: 'chat' })
            }
        }
        const mobileOpened = !isDesktop() && showMobileChat
        if (isDesktop() || mobileOpened) {
            if (unreadMessages.length > 0)
                markMessageAsRead(unreadMessages.map(notification => notification._id), roomId)
            if (unreadNotifications.length > 0)
                markAsRead(unreadNotifications[0]._id, unreadNotifications[0].referenceId)
        }
    }

    downloadFile = async (refId, mimeType) => {
        this.setState({ uploading: true })
        const { error, url } = await getFileURL(refId)
        this.setState({ uploading: false })
        if (!error) {
            const filePath = url
            window.open(filePath, '_blank')
        }
    }

    openFileModal = async () => {
        const { uploadFileModal, type, referenceId, roomId, addChatError, removeErrors } = this.props
        const result = await uploadFileModal({ accept: 'audio/*, image/*, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
        if (result && result.attachment) {
            this.setState({ uploading: true })
            const { error } = await this.props.addFileAction(roomId, {
                title: result.message,
                file: result.attachment,
                type,
                referenceId,
            })
            if (error) addChatError({ roomId, message: 'file_upload_fail' })
            this.setState({ uploading: false })
            setTimeout(() => {
                removeErrors(roomId)
            }, 5000)
        }
    }

    renderAllMessages = ({ day, userId, hash }) => {
        return day.map((m, index) => {
            let showHour = true
            const existNextMessage = day[index + 1]
            const sameUserNextMessage = existNextMessage ? existNextMessage.userId === m.userId : false
            if (existNextMessage) {
                const nextHour = moment(existNextMessage.create_at)
                const currentHour = moment(m.create_at)
                showHour = nextHour.diff(currentHour, 'minutes') <= 1 ? false : true
            }
            if (!m.mimeType)
                return renderMessage({ me: <TransLabel tag='me' />, m, index, userId, hash, showName: !sameUserNextMessage, showHour })
            return renderFile({ me: <TransLabel tag='me' />, m, index, userId, downloadFile: this.downloadFile })
        })
    }

    renderAllDays = ({ chat, userId }) => {
        const grouped = [...chat.messages, ...chat.files, ...chat.errors].sort((a, b) =>
            new Date(a.create_at) - new Date(b.create_at))
        const messages = uniqBy(grouped, (u) => u._id)
        const groupByDate = groupBy(messages, (result) => moment(result.create_at).format('ll'))
        if (!isEmpty(groupByDate))
            return Object.keys(groupByDate).map(day =>
                <div style={{ width: '100%' }} key={day}>
                    <span className="daytitle">{day}</span>
                    {this.renderAllMessages({ day: groupByDate[day], hash: chat.hash, userId })}
                </div>

            )
        else if (!chat.hash && chat.errors.length > 0)
            return chat.errors.map((err, index) => errorMessage(err.message, index))
        return null
    }
    showContent = () => {
        const {
            userId,
            chat,
            fetching,
            writing,
            username,
            roomId,
            referenceId,
            type,
            showMobileChat,
            toggleMobileChat,
        } = this.props
        const { uploading } = this.state
        if (!fetching) {
            return (
                <div className="chat">
                    <div className='title-container'>
                        <p className="titlechat"><i className="icon-padlock"></i><TransLabel tag="encrypted_chat" /></p>
                        {
                            showMobileChat &&
                            <button type="button" onClick={toggleMobileChat} className="close" aria-hidden="true">×</button>
                        }
                    </div>
                    <div className="scroll" id="chat">
                        <div className="chat-messages">
                            {chat && this.renderAllDays({ chat, userId })}
                            {uploading && <LoadingBox />}
                        </div>
                    </div>
                    {writing.length > 0 && (
                        <div className='writing-container'>
                            {writing.map(writer =>
                                <h5 key={writer}>
                                    {writer} <TransLabel tag='is_writing' />
                                </h5>)
                            }
                        </div>
                    )}
                    <div className="chat-input-holder">
                        <button className="attach-file" onClick={this.openFileModal}>
                            <i className="icon-attach"></i>
                        </button>
                        <InputBox
                            hash={chat.hash}
                            username={username}
                            userId={userId}
                            roomId={roomId}
                            type={type}
                            referenceId={referenceId}
                        />
                    </div>
                </div>
            )
        }
        else if (fetching || !chat.hash)
            return <LoadingBox />
        return null
    }

    render() {
        const { className, showMobileChat, isDesktop } = this.props
        if (!isDesktop() && !showMobileChat)
            return null
        return (
            <div className={showMobileChat ? `${className} show` : className}>
                <div className="chatcontainer">
                    {this.showContent()}
                </div>
            </div>
        )
    }
}

const mapStateToProps = (state, { trade, roomId }) => {
    const { notifications } = state.user.notifications
    const user = getProfile(state)
    const chatroom = roomId ? roomId : trade.chatroom
    const { fetching, error } = getUserChatsStatus(state)
    const chat = getUserChatroom(state, chatroom)
    const writing = chat.writing
    return {
        unreadNotifications: notifications.filter(e => e.referenceId === trade.id && !e.read),
        unreadMessages: chat.messages.filter(e => e.read === false && e.username !== user.username),
        chat,
        writing,
        roomId: chatroom,
        username: user.username,
        userId: user.id,
        role: user.role,
        fetching,
        referenceId: trade ? trade.id : null,
        error,
        showMobileChat: state.flags.showMobileChat,
        inDispute: getTradeIsInDispute(state, trade.id),

    }
}
const toggleMobileChat = () => dispatch => dispatch({ type: TOGGLE_MOBILE_CHAT })

const mapDispatchToProps = {
    addFileAction,
    getChat,
    uploadFileModal,
    joinRoom,
    toggleMobileChat,
    leaveRoom,
    markAsRead,
    markMessageAsRead,
    addChatError,
    removeErrors
}

export default compose(
    withGetScreen,
    connect(mapStateToProps, mapDispatchToProps),
)(ChatWindow);


// WEBPACK FOOTER //
// ./src/components/ChatWindow/ChatWindow.js